/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useContext, useEffect, useMemo } from 'react';
import Table, { TableProps } from 'antd/lib/table';
import { uniq } from 'lodash';
import { message } from 'antd';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { isFolderAvailable, TableEntry } from './FileTable.utils';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { DB_STATE } from '~src/types/enums';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { fadeInKeyframes } from '~src/styles/animations';
import { fileIcons } from '~src/config/fileTypeIcons';
import { FileTableProps } from './FileTable';
import { DbFile } from '~src/types/db';
import { sortByType, sortByModified } from './FileTable.utils';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import { useGetItemPath } from '~src/hooks/useGetItemPath';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { formatDate } from '~src/utils/formatDate';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';

interface TableEntryLocation extends TableEntry {
  location?: number | null | undefined | string;
}

interface DeletedFilesTableProps extends FileTableProps {
  cleanSelected: () => void;
}

export const DeletedFilesTable: React.FC<DeletedFilesTableProps> = ({
  selectedKeys,
  onSelect,
  onSelectAll,
  cleanSelected,
  highlightedRow,
  handleRow,
  setSelectedItems,
  openSVFTranscribe,
  ...tableProps
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();
  const { setFileToView } = useFileViewerContext();
  const getItemPath = useGetItemPath();
  const foldersQuery = useFolders(undefined, { enabled: true });

  const searchContext = useContext(SearchContext);
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { sortByRevision, sortByLoacion } = useSortByRevision();

  const filesQuery = useFiles(
    {
      state: [DB_STATE.REMOVED],
      showDeleted: true,
      withVersions: true,
      oldFilesImplementation: true,
    },
    { enabled: true }
  );

  const dbFileToTableEntry = (dbFile: DbFile): TableEntryLocation => {
    const {
      id,
      title,
      description,
      folder_id,
      created_at,
      state,
      updated_at,
      is_favorite,
      versions,
      location,
    } = dbFile;
    const nameSections = (title as string).split('.');
    const extension = nameSections.length
      ? nameSections[nameSections.length - 1].toLowerCase()
      : undefined;

    return {
      key: `file-${id}`,
      folder: false,
      type: (fileIcons[extension || 'default'] ||
        fileIcons.default) as typeof fileIcons.docx,
      extension,
      name: title,
      description,
      notifications: true,
      version: null,
      createdAt: created_at,
      updatedAt: updated_at,
      parentId: String(folder_id),
      id,
      state,
      disabled: false,
      dbFile,
      moduleFile: false,
      favourite: is_favorite,
      location: getItemPath(folder_id),
    };
  };

  const filesSource = useMemo(
    () =>
      (filesQuery.data || [])
        .filter((x) => x.state === DB_STATE.REMOVED)
        .map(dbFileToTableEntry),
    [filesQuery.data]
  );

  const filteredFiles = [
    ...filesSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];

  const columns = useMemo<TableProps<TableEntryLocation>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 60,
        align: 'center',
        render: (type, record) => {
          return {
            children: (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ width: 30, height: 30 }}>
                    {(type.icon === 'file-pdf' ||
                      type.icon === 'file-image') && (
                      <IconButton
                        click={() => {
                          if (record.dbFile?.versions?.length) {
                            setFileToView({ ...record.dbFile });
                          } else {
                            message.error(
                              intl.formatMessage({ id: 'file-preview-error' })
                            );
                          }
                        }}
                        iconStyle={type.iconStyle}
                        icon={['far', 'eye']}
                        color="red"
                      />
                    )}

                    {(() => {
                      const version = fileVersionsContext[record.id];
                      const hasModel = version?.extension_svf_model != null;
                      const isConvertible =
                        version?.is_svf_convertible === true;
                      const isViewable =
                        version?.extension_svf_model?.state === 7;
                      const shouldShow = isConvertible || isViewable;
                      if (!shouldShow) return null;

                      return (
                        <SVFIconType
                          key={`svf-${record.id}-${
                            version?.extension_svf_model?.state ?? 'none'
                          }`}
                          version={version}
                          extension={record.extension}
                          variant="sidebar"
                          disabled={false}
                          customTooltip={(() => {
                            const state = version?.extension_svf_model?.state;
                            if (!hasModel && isConvertible)
                              return intl.formatMessage({
                                id: 'svf-view-file-convert',
                              });
                            if ([1, 2, 4].includes(state as number))
                              return intl.formatMessage({
                                id: 'svf-view-file-progress',
                              });
                            if (state === 7)
                              return intl.formatMessage({
                                id: 'svf-view-file-window',
                              });
                            if (state === 5)
                              return intl.formatMessage({
                                id: 'svf-view-file-convert',
                              });
                            return intl.formatMessage({ id: 'svf-view-file' });
                          })()}
                          onClick={() => {
                            // console.log(`DBfILE`, record.dbFile);
                            if (!record.dbFile) {
                              message.error(
                                intl.formatMessage({ id: 'file-preview-error' })
                              );
                              return;
                            }
                            const v = fileVersionsContext[record.id];
                            const version = fileVersionsContext[record.id];
                            const isConvertible =
                              version?.is_svf_convertible === true;
                            const state = version?.extension_svf_model?.state;
                            const ready = state === 7;

                            if (ready) {
                              if (record.dbFile?.versions?.length) {
                                setFileToView({ ...record.dbFile });
                              } else {
                                message.error(
                                  intl.formatMessage({
                                    id: 'file-preview-error',
                                  })
                                );
                              }
                              return;
                            }

                            if (
                              openSVFTranscribe &&
                              (isConvertible || state === 5)
                            ) {
                              openSVFTranscribe({ ...record.dbFile });
                              setFileToView(undefined);
                            }
                          }}
                          type={type}
                        />
                      );
                    })()}
                  </div>

                  {(() => {
                    const ext = record.extension?.toLowerCase() || '';
                    const isCadFile = ['rvt', 'dwg', 'ifc'].includes(ext);

                    if (isCadFile) {
                      return (
                        <SVFIconType
                          key={`file-icon-${record.id}`}
                          version={fileVersionsContext[record.id]}
                          extension={record.extension}
                          variant="table"
                          customTooltip={intl.formatMessage(
                            { id: type.icon },
                            { type: record.extension?.toUpperCase() }
                          )}
                          onClick={() => {
                            //
                          }}
                          type={type}
                        />
                      );
                    }
                    return (
                      <IconButton
                        iconStyle={type.iconStyle}
                        icon={type.icon}
                        iconSecond={
                          record.state === DB_STATE.DELETED
                            ? ['fad', 'trash']
                            : null
                        }
                        colorSecondIcon="red"
                        customStyleSecondIcon={{ left: '12px', top: '10px' }}
                        color={type.color}
                        customStyle={style.icons}
                        tooltip={intl.formatMessage(
                          { id: type.icon },
                          { type: record.extension?.toUpperCase() }
                        )}
                        badge={
                          record.state !== DB_STATE.DELETED &&
                          type.is_empty &&
                          '0'
                        }
                        backgroundOff
                      />
                    );
                  })()}
                </div>
              </div>
            ),
          };
        },
        sorter: sortByType,
      },
      // {
      //   title: intl.formatMessage({ id: 'action' }),
      //   width: 72,
      //   align: 'center',
      //   dataIndex: 'type',
      //
      //   render: (type, record) =>
      //     (type.icon === 'file-pdf' || type.icon === 'file-image') && (
      //       <IconButton
      //         click={() => {
      //           if (
      //             record.dbFile?.versions &&
      //             record.dbFile?.versions?.length > 0
      //           ) {
      //             setFileToView(record.dbFile);
      //           } else {
      //             message.error(
      //               intl.formatMessage({ id: 'file-preview-error' })
      //             );
      //           }
      //         }}
      //         iconStyle={type.iconStyle}
      //         icon={['far', 'eye']}
      //         color="red"
      //       />
      //     ),
      // },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 250,
        render: (name, record) => (
          <Fragment>
            {name}
            {record.favourite && (
              <IconButton icon={['fas', 'star']} color="yellow" backgroundOff />
            )}
          </Fragment>
        ),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        // width: 200,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'path' }),
        dataIndex: 'parentId',
        key: 'path',
        sorter: (a, b) => sortByLoacion(a, b),
        render: (stringParentId: TableEntryLocation['parentId']) => {
          const parentId =
            stringParentId === 'null' ? null : Number(stringParentId);

          const availableFolder =
            foldersQuery.data && isFolderAvailable(parentId, foldersQuery.data);
          if (availableFolder) {
            return getItemPath(parentId);
          } else {
            if (availableFolder === null) {
              return (
                <Fragment>
                  <IconButton
                    icon="ban"
                    customStyle={style.typeIcon}
                    color="red"
                    tooltip={intl.formatMessage({ id: 'modified-date' })}
                    backgroundOff
                  />
                  {getItemPath(parentId)}
                </Fragment>
              );
            } else {
              return (
                <Fragment>
                  <IconButton
                    icon={['fad', 'trash']}
                    customStyle={style.typeIcon}
                    color="red"
                    tooltip={intl.formatMessage({ id: 'folder-deleted' })}
                    backgroundOff
                  />
                  {getItemPath(parentId)}
                </Fragment>
              );
            }
          }
        },
      },
      {
        title: intl.formatMessage({ id: 'revision' }),
        dataIndex: 'dbFile',
        sorter: (a, b) => sortByRevision(a, b),
        width: 200,
        ellipsis: true,
        render: (x, record) => {
          if (!record.dbFile?.versions) return null; // only for files
          return (
            fileVersionsContext[record.id]?.version ||
            record.dbFile.versions[0]?.version
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'ver' }),
        dataIndex: 'dbFile',
        width: 80,
        align: 'center',
        onCell: () => ({ style: { padding: '4px 10px' } }),
        render: (file) => {
          if (!file) return null;
          return <VersionSelect file={file} />;
        },
      },
      {
        title: intl.formatMessage({ id: 'modified-date' }),
        dataIndex: 'updatedAt',
        sorter: sortByModified(fileVersionsContext),
        width: 140,
        align: 'center',
        render: (updatedAt, record) => {
          let modified = updatedAt;

          // if file, show "modified" of selected version
          if (record.dbFile?.versions) {
            modified =
              fileVersionsContext[record.id]?.updated_at ||
              record.dbFile.versions[0]?.updated_at;
          }

          return formatDate(modified, 'short');
        },
      },
    ],
    [
      fileVersionsContext,
      intl,
      style.icons,
      style.small,
      foldersQuery.data,
      filesQuery.data,
    ]
  );

  // clean cherry pick selected items on component dismount
  useEffect(() => {
    return () => {
      cleanSelected();
    };
  }, []);

  const handleSelectShift = (record: TableEntryLocation, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  };

  const getTableData = () => {
    return [...filteredFiles];
  };

  return (
    <Table<TableEntryLocation>
      showSorterTooltip={false}
      css={[
        style.table,
        tableProps.scroll?.y && {
          '.ant-table-body': {
            height: tableProps.scroll.y,
          },
        },
      ]}
      bordered
      className={'main-content-window su-table'}
      columns={columns}
      rowClassName={(record) => {
        let className = 'su-row-table ';
        if (record.key === highlightedRow?.key) {
          className += 'ant-table-row-selected' + ' ';
        }
        if (record.disabled) {
          className += 'unfinished' + ' ';
        }

        return className;
      }}
      // rowClassName="su-row-table"
      dataSource={filteredFiles}
      pagination={false}
      loading={filesQuery.isLoading || filesQuery.isIdle}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: selectedKeys,
        columnWidth: 50,
        onSelect,
        onSelectAll,
      }}
      {...tableProps}
      onRow={(record, index) => ({
        ...handleRow(record, index),
        onClick: (event) => {
          if (event.shiftKey) {
            return handleSelectShift(record, index);
          }
          const onClickAction = handleRow(record, index).onClick;
          if (onClickAction) {
            return onClickAction(event);
          }
        },
      })}
    />
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  icons: {
    color: theme.colors.secondary,
    fontSize: '20px',
    margin: '0 2px',
  },
  small: {
    fontSize: '16px',
  },
  table: {
    '.ant-table-row': {
      animation: `${fadeInKeyframes} 0.3s ease-out`,
      cursor: 'pointer',

      '&.unfinished > td': {
        color: 'rgba(0, 0, 0, 0.25)',
      },
    },
  },
});
